/**
 * Summarise a stock class's monthly plan for display.
 *
 * Some classes (e.g. Growing Cattle/Lambs spawned by a breeding preset) are 0
 * "today" and only arrive later in the year. For those we surface the count and
 * the month they're born rather than a confusing 0.
 */

export interface StockClassMonthLike {
  month: number;
  yearOffset?: number;
  animalCount: number;
  averageWeightKg: number;
  allocationPercent: number;
}

export interface StockClassSummary {
  /** Animal count for the current month, or the arrival count if born later. */
  count: number;
  allocationPercent: number;
  /** Daily demand (kgDM/day) for `count` at that allocation. */
  demand: number;
  /** 0-11 when the class is absent today and arrives later, otherwise null. */
  bornMonth: number | null;
}

function demandFor(entry?: StockClassMonthLike): number {
  if (!entry) return 0;
  return Math.round(
    (entry.animalCount * entry.averageWeightKg * entry.allocationPercent) / 100
  );
}

export function summariseStockClass(
  months: StockClassMonthLike[],
  currentMonth: number
): StockClassSummary {
  const current =
    months.find((m) => m.month === currentMonth && (m.yearOffset ?? 0) === 0) ??
    months[0];

  if ((current?.animalCount ?? 0) > 0) {
    return {
      count: current.animalCount,
      allocationPercent: current.allocationPercent ?? 0,
      demand: demandFor(current),
      bornMonth: null,
    };
  }

  // Not present today — find the first month they arrive.
  const firstPresent = [...months]
    .sort(
      (a, b) => (a.yearOffset ?? 0) - (b.yearOffset ?? 0) || a.month - b.month
    )
    .find((m) => (m.animalCount ?? 0) > 0);

  if (firstPresent) {
    return {
      count: firstPresent.animalCount,
      allocationPercent: firstPresent.allocationPercent ?? 0,
      demand: demandFor(firstPresent),
      bornMonth: firstPresent.month,
    };
  }

  return {
    count: current?.animalCount ?? 0,
    allocationPercent: current?.allocationPercent ?? 0,
    demand: 0,
    bornMonth: null,
  };
}
